import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "./Dashboard";
import { Plus, Pencil, Trash2, Send, Eye, EyeOff, FileText, Copy } from "lucide-react";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialList, EditorialRow, EditorialAction } from "@/components/admin/EditorialList";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";
import useLottieOnce from "@/hooks/useLottieOnce";
import quillFlourishLottie from "@/assets/lottie/quill-flourish.json";
import particleBurstLottie from "@/assets/lottie/particle-burst.json";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";
import EditorialDialog, { EditorialField } from "@/components/admin/EditorialDialog";
import EditorialSelect from "@/components/admin/EditorialSelect";

const PLACEHOLDER_HINTS = [
  "{{client_name}}", "{{client_email}}", "{{client_phone}}",
  "{{service}}", "{{tier}}", "{{session_date}}", "{{session_time}}",
  "{{total}}", "{{deposit}}", "{{studio_name}}", "{{contract_number}}", "{{today}}",
];

export default function AdminContracts() {
  return (
    <div className="space-y-8" data-testid="admin-contracts-page">
      <AdminPageHeader
        breadcrumb={["WORKFLOW", "CONTRACTS"]}
        title="Templates & agreements"
      />
      <p className="text-sm text-muted-foreground -mt-4 max-w-xl">
        Create reusable contract templates with smart placeholders. Draft from a booking, email a signing link, and let your client e-sign in seconds.
      </p>

      <Tabs defaultValue="contracts">
        <TabsList>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="contracts" className="mt-6"><ContractsPanel /></TabsContent>
        <TabsContent value="templates" className="mt-6"><TemplatesPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ContractsPanel() {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [draft, setDraft] = useState({ template_id: "", booking_id: "", title: "", customer_name: "", customer_email: "" });

  // Track previous status per contract id so a draft→signed transition (e.g. a
  // client signing in the background, surfaced on the next list reload) fires
  // a one-shot particle burst. Pre-seeded on first load so initial fetch does
  // not flash the celebration.
  const prevStatuses = useRef(null);
  const [fireSignedBurst, signedBurstSurface] = useLottieOnce(particleBurstLottie, { size: 120 });

  async function load() {
    const [c, t, b] = await Promise.all([
      api.get("/admin/contracts"),
      api.get("/admin/contract-templates"),
      api.get("/admin/bookings?status_filter=all"),
    ]);
    if (prevStatuses.current) {
      // detect signed transitions
      for (const next of c.data) {
        const prev = prevStatuses.current[next.id];
        if (prev && prev !== "signed" && next.status === "signed") {
          fireSignedBurst();
          break;
        }
      }
    }
    prevStatuses.current = Object.fromEntries((c.data || []).map((x) => [x.id, x.status]));
    setContracts(c.data);
    setTemplates(t.data);
    setBookings(b.data);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!draft.template_id) return toast.error("Select a template");
    if (!draft.booking_id && (!draft.customer_name || !draft.customer_email)) {
      return toast.error("Pick a booking, or enter client name + email");
    }
    try {
      const payload = {
        template_id: draft.template_id,
        booking_id: draft.booking_id || undefined,
        title: draft.title || undefined,
        customer_name: draft.customer_name || undefined,
        customer_email: draft.customer_email || undefined,
      };
      await api.post("/admin/contracts", payload);
      toast.success("Contract created");
      setOpen(false);
      setDraft({ template_id: "", booking_id: "", title: "", customer_name: "", customer_email: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function send(id) {
    try {
      const { data } = await api.post(`/admin/contracts/${id}/send`);
      toast.success("Contract emailed to client");
      load();
      // Copy sign link to clipboard for convenience — uses the iframe-safe helper
      copyToClipboardWithToast(data.sign_url, "Sign link copied");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function voidIt(id) {
    if (!window.confirm("Void this contract?")) return;
    try {
      await api.delete(`/admin/contracts/${id}`);
      toast.success("Contract voided");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function view(c) {
    try {
      const { data } = await api.get(`/admin/contracts/${c.id}`);
      setViewing(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90"
          data-testid="new-contract-btn"
        >
          <Plus className="w-4 h-4" /> New contract
        </button>
      </div>

      {contracts.length === 0 ? (
        <EditorialEmptyState
          lottie={quillFlourishLottie}
          eyebrow="The folio is open"
          headline={<>No contracts <em>yet</em>.</>}
          description='Click "New contract" to draft an agreement from a saved template.'
          tone="charcoal"
          backdrop={EDITORIAL_ASSETS.editorial_detail_hands.src}
          data-testid="contracts-empty"
        />
      ) : (
        <div className="relative" data-testid="contracts-signed-burst-anchor">
          {signedBurstSurface}
          <EditorialList>
          {contracts.map((c) => (
            <EditorialRow
              key={c.id}
              prefix={c.contract_number}
              title={c.customer_name}
              meta={[
                c.customer_email,
                c.template_name,
                c.signed_at ? `signed ${new Date(c.signed_at).toLocaleDateString()}` : null,
              ]}
              status={<StatusBadge value={c.status} />}
              actions={
                <>
                  <EditorialAction
                    onClick={() => view(c)}
                    data-testid={`view-${c.contract_number}`}
                    icon={<Eye className="w-3 h-3 mr-1" />}
                  >
                    View
                  </EditorialAction>
                  {c.status !== "signed" && c.status !== "void" && (
                    <button
                      type="button"
                      onClick={() => send(c.id)}
                      data-testid={`send-${c.contract_number}`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 border border-[color:var(--gold-base)]/60 hover:border-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/10 text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] transition-colors"
                    >
                      <Send className="w-3 h-3" />
                      Send contract
                    </button>
                  )}
                  {c.status !== "signed" && (
                    <button
                      onClick={() => voidIt(c.id)}
                      data-testid={`void-${c.contract_number}`}
                      title="Void"
                      className="text-muted-foreground/70 hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              }
              data-testid={`contract-row-${c.contract_number}`}
            />
          ))}
        </EditorialList>
        </div>
      )}

      {/* Create dialog */}
      <EditorialDialog
        open={open}
        onClose={() => setOpen(false)}
        breadcrumb="CONTRACTS · NEW"
        title="New contract"
        subtitle="Drafts can be sent for e-signature from the contract row's actions."
        dirty={JSON.stringify(draft) !== JSON.stringify({ template_id: "", booking_id: "", title: "", customer_name: "", customer_email: "" })}
        onDiscard={() => setDraft({ template_id: "", booking_id: "", title: "", customer_name: "", customer_email: "" })}
        onSave={create}
        saveLabel="SAVE CONTRACT →"
        size="md"
      >
        <div className="space-y-7">
          <EditorialField label="Template" required>
            <EditorialSelect
              value={draft.template_id}
              onChange={(v) => setDraft({ ...draft, template_id: v })}
              options={templates.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="Choose template…"
              data-testid="contract-template-select"
            />
          </EditorialField>
          <EditorialField label="Linked booking" hint="Auto-fills placeholders from the booking — optional.">
            <EditorialSelect
              value={draft.booking_id}
              onChange={(v) => setDraft({ ...draft, booking_id: v })}
              options={[
                { value: "", label: "— none —" },
                ...bookings.map((b) => ({
                  value: b.id,
                  label: `${b.customer_name} · ${b.service_name}`,
                  descriptor: b.reference,
                })),
              ]}
              placeholder="Optional — pick a booking"
              data-testid="contract-booking-select"
            />
          </EditorialField>
          <div className="grid grid-cols-2 gap-6">
            <EditorialField label="Or client name">
              <input value={draft.customer_name} onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} className="input" data-testid="contract-name" />
            </EditorialField>
            <EditorialField label="Or client email">
              <input value={draft.customer_email} onChange={(e) => setDraft({ ...draft, customer_email: e.target.value })} className="input" data-testid="contract-email" />
            </EditorialField>
          </div>
          <EditorialField label="Title override" hint="Leave blank to use template default.">
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="input" data-testid="contract-title" />
          </EditorialField>
          <button onClick={create} className="hidden" data-testid="contract-create-btn" aria-hidden tabIndex={-1}>Create</button>
        </div>
      </EditorialDialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-light">{viewing.title}</DialogTitle>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{viewing.contract_number} · {viewing.status}</div>
              </DialogHeader>
              {viewing.sign_url && (
                <div className="bg-muted px-4 py-3 flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground shrink-0">Sign URL:</span>
                  <code className="flex-1 truncate">{viewing.sign_url}</code>
                  <button
                    onClick={() => copyToClipboardWithToast(viewing.sign_url, "Copied")}
                    className="inline-flex items-center gap-1 px-2 h-7 border border-border hover:border-foreground/60"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              )}
              <pre className="whitespace-pre-wrap text-sm font-serif leading-relaxed max-h-[55vh] overflow-y-auto p-2">{viewing.body}</pre>
              {viewing.signed_at && (
                <div className="border-t border-border pt-4 mt-2">
                  <div className="eyebrow mb-2">Signed</div>
                  <div className="text-sm">{viewing.signature_name} · {new Date(viewing.signed_at).toLocaleString()}</div>
                  {viewing.signature_image && (
                    <img src={viewing.signature_image} alt="signature" className="mt-2 h-16 object-contain bg-white border border-border" />
                  )}
                  <div className="text-xs text-muted-foreground mt-1">IP: {viewing.signed_ip || "—"}</div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    const { data } = await api.get("/admin/contract-templates");
    setTemplates(data);
  }
  useEffect(() => { load(); }, []);

  async function save(orig, draft) {
    try {
      const payload = {
        name: draft.name,
        description: draft.description || "",
        body: draft.body,
        is_active: !!draft.is_active,
      };
      if (orig?.id) {
        await api.patch(`/admin/contract-templates/${orig.id}`, payload);
      } else {
        await api.post("/admin/contract-templates", payload);
      }
      toast.success("Saved");
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function remove(t) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/contract-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function toggleActive(t) {
    // True partial PATCH — only flip is_active. Backed by ContractTemplateUpdate
    // (all-Optional). Optimistically updates the row so the toggle feels snappy.
    try {
      await api.patch(`/admin/contract-templates/${t.id}`, { is_active: !t.is_active });
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_active: !x.is_active } : x)));
      toast.success(t.is_active ? "Template deactivated" : "Template activated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update template");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-5 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em]" data-testid="new-template-btn">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="border border-[color:var(--gold-foil)]/25 hover:border-[color:var(--gold-foil)]/50 p-5 transition-colors" data-testid={`template-${t.id}`}>
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-[color:var(--gold-base)] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-xl italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{t.name}</div>
                {t.description && <div className="text-[11px] text-muted-foreground/70 mt-1">{t.description}</div>}
                <div className="text-xs text-muted-foreground mt-3 line-clamp-3 font-serif italic">{t.body}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-3 justify-end items-center">
              <button
                onClick={() => toggleActive(t)}
                role="switch"
                aria-checked={t.is_active}
                aria-label={t.is_active ? "Deactivate template (hide from contract dropdown)" : "Activate template (show in contract dropdown)"}
                title={t.is_active ? "Visible in the contract dropdown — click to hide" : "Hidden from the contract dropdown — click to show"}
                data-testid={`toggle-template-active-${t.id}`}
                className={`group inline-flex items-center gap-2 h-8 pl-2 pr-3 border text-[10px] uppercase tracking-[0.28em] transition-colors ${t.is_active
                  ? "border-[color:var(--gold-base)]/60 text-[color:var(--gold-base)] hover:border-[color:var(--gold-base)]"
                  : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}
              >
                <span
                  className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${t.is_active ? "bg-[color:var(--gold-base)]/70" : "bg-muted-foreground/25"}`}
                  aria-hidden="true"
                >
                  <span
                    className={`inline-block h-3 w-3 rounded-full bg-background shadow transform transition-transform ${t.is_active ? "translate-x-3.5" : "translate-x-0.5"}`}
                  />
                </span>
                {t.is_active ? <Eye className="w-3 h-3" aria-hidden="true" /> : <EyeOff className="w-3 h-3" aria-hidden="true" />}
                <span>{t.is_active ? "Visible" : "Hidden"}</span>
              </button>
              <EditorialAction onClick={() => setEditing(t)} data-testid={`edit-template-${t.id}`} icon={<Pencil className="w-3 h-3 mr-1" />}>Edit</EditorialAction>
              <button
                onClick={() => remove(t)}
                data-testid={`delete-template-${t.id}`}
                title="Delete template"
                className="text-muted-foreground/70 hover:text-destructive transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <TemplateDialog
        open={!!editing || creating}
        onClose={() => { setEditing(null); setCreating(false); }}
        original={editing}
        onSave={(draft) => save(editing, draft)}
      />
    </div>
  );
}

function TemplateDialog({ open, onClose, original, onSave }) {
  const [draft, setDraft] = useState({ name: "", description: "", body: "", is_active: true });
  const [initial, setInitial] = useState({ name: "", description: "", body: "", is_active: true });

  useEffect(() => {
    if (open) {
      const next = original ? { ...original } : { name: "", description: "", body: "", is_active: true };
      setDraft(next);
      setInitial({ ...next });
    }
  }, [open, original]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);

  function insertPlaceholder(p) {
    setDraft((d) => ({ ...d, body: (d.body || "") + p }));
  }

  return (
    <EditorialDialog
      open={open}
      onClose={onClose}
      breadcrumb={original ? "CONTRACTS · TEMPLATE" : "CONTRACTS · NEW TEMPLATE"}
      title={original ? "Edit template" : "New template"}
      subtitle="Templates pre-fill new contracts. Use {{client_name}}, {{date}}, {{total}} as token placeholders — they get substituted when you draft a contract from a booking."
      dirty={dirty}
      onDiscard={() => setDraft(initial)}
      onSave={() => onSave(draft)}
      saveLabel="SAVE TEMPLATE →"
      size="lg"
    >
      <div className="space-y-7">
        <EditorialField label="Name" required>
          <input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="input" data-testid="template-name" />
        </EditorialField>
        <EditorialField label="Description">
          <input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="input" data-testid="template-description" />
        </EditorialField>
        <div>
          <span className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-2">Body</span>
          <textarea
            value={draft.body || ""}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            className="input"
            style={{ minHeight: "24rem", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="template-body"
            placeholder="PHOTOGRAPHY SESSION AGREEMENT&#10;&#10;This agreement is made between {{studio_name}} and {{client_name}}..."
          />
          <div className="mt-3 flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--cream)]/50 mr-2">Insert:</span>
            {PLACEHOLDER_HINTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => insertPlaceholder(p)}
                className="px-2 py-0.5 text-[11px] font-mono border border-[color:var(--gold-foil)]/30 hover:border-[color:var(--gold-base)] hover:text-[color:var(--gold-base)] text-[color:var(--cream)]/75 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
          <button onClick={() => onSave(draft)} className="hidden" data-testid="template-save-btn" aria-hidden tabIndex={-1}>Save</button>
        </div>
      </div>
    </EditorialDialog>
  );
}
